import { useState, useEffect } from 'react';
import { X, Sparkles, Building2, Users, ArrowRight } from 'lucide-react';
import api from '@/lib/api';

export default function WelcomeModal({ user, workspace }) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only show if user just signed up and hasn't created a workspace yet
    if (user && (!workspace || !workspace.name) && !localStorage.getItem('vocala_welcome_seen')) {
      setIsOpen(true);
    }
  }, [user, workspace]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('vocala_welcome_seen', 'true');
  };

  const handleCreateWorkspace = async () => {
    if (!companyName.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post('/workspaces', { name: companyName.trim() });
      localStorage.setItem('vocala_workspace_id', data.workspace_id);
      window.location.reload(); // Refresh to load the new workspace into app context
    } catch (err) {
      logger.error(err);
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" style={{ fontFamily: 'Inter, sans-serif' }} data-testid="welcome-modal">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
        onClick={step === 1 ? handleClose : undefined}
      />

      {/* Modal Card */}
      <div className="relative w-full max-w-2xl bg-white/95 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-slate-200/60 overflow-hidden flex flex-col md:flex-row transform transition-all">

        {/* Left/Top Hero Banner */}
        <div className="w-full md:w-5/12 bg-gradient-to-br from-blue-600 via-[#1e40af] to-emerald-600 p-8 text-white flex flex-col justify-between relative overflow-hidden">
          {/* Subtle background rings */}
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-emerald-400/20 rounded-full blur-2xl" />

          <div className="relative z-10">
            <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mb-6 shadow-lg">
              <Sparkles className="w-6 h-6 text-emerald-300" />
            </div>
            <h2 className="text-3xl font-black tracking-tight mb-2">Welcome to Vocala</h2>
            <p className="text-blue-100 text-sm leading-relaxed">
              The AI-native recruiting platform. Source, screen, and hire faster than ever before.
            </p>
          </div>

          <div className="mt-12 hidden md:block relative z-10">
            <div className="flex -space-x-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-blue-400 border-2 border-blue-600 flex items-center justify-center text-[10px] font-bold">JD</div>
              <div className="w-8 h-8 rounded-full bg-emerald-400 border-2 border-blue-600 flex items-center justify-center text-[10px] font-bold">AS</div>
              <div className="w-8 h-8 rounded-full bg-amber-400 border-2 border-blue-600 flex items-center justify-center text-[10px] font-bold">+4</div>
            </div>
            <p className="text-xs text-blue-200 font-medium">Join 2,000+ top recruiting teams</p>
          </div>
        </div>

        {/* Right/Bottom Content */}
        <div className="w-full md:w-7/12 p-8 sm:p-10 flex flex-col justify-center bg-white relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {step === 1 ? (
            <div>
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-5 border border-blue-100 shadow-sm">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2 tracking-tight">Let's get set up</h3>
              <p className="text-slate-500 text-sm mb-8 leading-relaxed">
                Hi {user?.name?.split(' ')[0] || 'there'}, we're excited to have you onboard! First, we need to create a workspace for your team.
              </p>
              <button
                onClick={() => setStep(2)}
                className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 text-white py-3.5 px-4 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-[1.01] active:scale-[0.98] transition-all flex items-center justify-center gap-2 group"
              >
                Create Workspace
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={handleClose}
                className="w-full mt-3 text-sm text-slate-500 hover:text-slate-800 py-2 transition-colors font-medium"
              >
                Skip for now
              </button>
            </div>
          ) : (
            <div>
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-5 border border-emerald-100 shadow-sm">
                <Building2 className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2 tracking-tight">Name your team</h3>
              <p className="text-slate-500 text-sm mb-6">
                This is where you'll collaborate on jobs and candidates. You can change this later.
              </p>

              <div className="mb-8">
                <label className="block text-sm font-bold text-slate-700 mb-2">Company or Team Name</label>
                <input
                  autoFocus
                  type="text"
                  placeholder="e.g. Acme Corp Recruiting"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateWorkspace()}
                  className="w-full px-4 py-3.5 border border-slate-200/70 rounded-xl bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-900 placeholder-slate-400 hover:bg-white transition-all shadow-sm"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="px-5 py-3.5 rounded-xl font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  onClick={handleCreateWorkspace}
                  disabled={!companyName.trim() || loading}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-emerald-600 disabled:from-slate-400 disabled:to-slate-400 text-white py-3.5 px-4 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-[1.01] active:scale-[0.98] transition-all flex items-center justify-center"
                >
                  {loading ? (
                    <Sparkles className="w-5 h-5 animate-pulse" />
                  ) : (
                    'Complete Setup'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
